import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUrlSyncedId } from '../../hooks/useUrlSyncedId';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  Center,
  Icon,
  IconButton,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  Portal,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { SearchIcon, WarningIcon, ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import {
  listProcesses,
  getProcessStatus,
  cancelProcess,
  retryProcess,
  deleteSourceFile,
  fetchCategories,
  fetchTags,
  fetchSourceFilesCoverage,
  fetchSourceFileAccounts,
} from '../../api/endpointsBackend';
import { formatRelativeTime } from '../../utils/statementHelpers';
import { formatMoney } from '../../utils/formatters';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import SourceFilesUploader from './SourceFilesUploader';
import SourceFileDetailPanel from './SourceFileDetailPanel';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { fileMeta, StatusPill, isActiveStatus, TINT } from './sourceFilesUI';
import PageHeader from '../shared/PageHeader';
import { cardSurface } from '../shared/Card';

import { useResizablePanel } from '../../hooks/useResizablePanel';
import PanelResizeHandle, { pagePanelOptions, resizablePanelWidth } from '../shared/PanelResizeHandle';
import useIsPhone from '../../hooks/useIsPhone';
import { useStatementRepairFlags } from '../../hooks/useStatementRepair';
import PhoneStatements from '../phone/PhoneStatements';
import StatementFlagChips from '../repairs/StatementFlagChips';
import CheckupBanner from '../repairs/checkup/CheckupBanner';
const POLL_INTERVAL_MS = 2500;

// "YYYY-MM" so groups sort lexicographically as well as chronologically
const monthKeyOf = (dateString) => {
  if (!dateString) return 'unknown';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthKeyNow = () => monthKeyOf(new Date().toISOString());
const monthKeyLast = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return monthKeyOf(d.toISOString());
};

const monthLabelFor = (monthKey) => {
  if (monthKey === 'unknown') return i18n.t('sourceFiles:month.unknown');
  const [year, month] = monthKey.split('-').map((s) => parseInt(s, 10));
  const months = i18n.t('months.long', { ns: 'dates', returnObjects: true });
  return `${months[month - 1]} ${year}`;
};

const monthSuffixFor = (monthKey) => {
  if (monthKey === monthKeyNow()) return i18n.t('sourceFiles:month.thisMonth');
  if (monthKey === monthKeyLast()) return i18n.t('sourceFiles:month.lastMonth');
  return null;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const months = i18n.t('months.short', { ns: 'dates', returnObjects: true });
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

const formatShortDate = (input) => {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  const months = i18n.t('months.short', { ns: 'dates', returnObjects: true });
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const uploadDateOf = (p) => p.source_file?.upload_date || p.started_at || null;

function MonthGroup({ monthKey, items, selectedProcessId, onSelect, repairFlags }) {
  const [isOpen, setIsOpen] = useState(true);
  const suffix = monthSuffixFor(monthKey);
  return (
    <Box mb={4}>
      <Flex
        as="button"
        type="button"
        align="center"
        gap={1.5}
        mb={3}
        onClick={() => setIsOpen((o) => !o)}
        cursor="pointer"
        _hover={{ color: 'redesign.heading' }}
      >
        <ChevronDownIcon
          boxSize="12px"
          color="redesign.text.muted"
          transform={isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
          transition="transform 0.15s"
        />
        <Text textStyle="microLabel">
          {monthLabelFor(monthKey)}
          {suffix ? ` · ${suffix}` : ''}
        </Text>
      </Flex>
      {isOpen && (
        <VStack spacing="2px" align="stretch">
          {items.map((p) => (
            <FileRow
              key={p.process_id}
              process={p}
              isSelected={p.process_id === selectedProcessId}
              onSelect={onSelect}
              repairFlags={repairFlags?.[p.source_file?.id]}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

const ACCOUNT_BADGE_LIMIT = 2;

const accountBadgeStyle = {
  bg: 'redesign.bg.card',
  color: 'redesign.text',
  borderWidth: '1px',
  borderColor: 'redesign.text.faint',
  fontSize: '0.65rem',
  borderRadius: 'full',
  px: '8px',
  py: '1px',
  lineHeight: '1.4',
  fontWeight: 'medium',
  opacity: 0.85,
  flexShrink: 0,
  textTransform: 'none',
};

function AccountBadge({ acct }) {
  return (
    <Badge {...accountBadgeStyle}>
      {acct.icon ? `${acct.icon} ` : ''}{acct.name}
    </Badge>
  );
}

function ExtraAccountsPopover({ accounts }) {
  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Badge
          as="button"
          {...accountBadgeStyle}
          cursor="pointer"
          _hover={{ bg: 'redesign.bg.hover' }}
          onClick={(e) => e.stopPropagation()}
        >
          +{accounts.length}
        </Badge>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="auto"
          minW="180px"
          maxW="260px"
          borderColor="redesign.border"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody p={1}>
            <VStack spacing={0} align="stretch">
              {accounts.map((a) => (
                <Flex
                  key={a.id}
                  align="center"
                  gap={2}
                  px={2}
                  py={1.5}
                  borderRadius="md"
                >
                  <Text fontSize="sm" color="redesign.text" noOfLines={1}>
                    {a.icon ? `${a.icon} ` : ''}{a.name}
                  </Text>
                </Flex>
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function FileRow({ process, isSelected, onSelect, repairFlags }) {
  const { t } = useTranslation('sourceFiles');
  const sf = process.source_file;
  const meta = fileMeta(sf?.file_type);
  const uploadDate = uploadDateOf(process);
  const stamp = formatDateTime(uploadDate);
  const ago = uploadDate ? formatRelativeTime(uploadDate) : '';
  return (
    <Flex
      as="button"
      onClick={() => onSelect(process)}
      align="center"
      gap={3}
      w="100%"
      px={3}
      py="10px"
      borderRadius="lg"
      textAlign="left"
      bg={isSelected ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: isSelected ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
      transition="background-color 0.15s"
    >
      <Flex w="32px" h="32px" borderRadius="lg" bg={meta.bg} _dark={meta._dark} align="center" justify="center" flexShrink={0}>
        <Icon as={meta.icon} boxSize="16px" color={meta.color} />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontSize="sm" color="redesign.heading" noOfLines={1}>
          {sf?.filename || t('list.unknownFile')}
        </Text>
        <Flex align="center" gap={1.5} flexWrap="wrap">
          <Text fontSize="2xs" color="redesign.text.muted">
            {stamp}
            {stamp && ago ? ' · ' : ''}
            {ago}
          </Text>
          {(process.accounts || []).slice(0, ACCOUNT_BADGE_LIMIT).map((acct) => (
            <AccountBadge key={acct.id} acct={acct} />
          ))}
          {process.accounts && process.accounts.length > ACCOUNT_BADGE_LIMIT && (
            <ExtraAccountsPopover accounts={process.accounts.slice(ACCOUNT_BADGE_LIMIT)} />
          )}
          <StatementFlagChips flags={repairFlags} />
        </Flex>
      </Box>
      <StatusPill status={process.status} />
    </Flex>
  );
}

// The phone gets its own page rather than a responsive branch inside this
// one, so none of the desktop body's hooks run there (its URL writes among them).
export default function SourceFilesPage(props) {
  return useIsPhone() ? <PhoneStatements {...props} /> : <SourceFilesPageDesktop {...props} />;
}

function SourceFilesPageDesktop() {
  const { t } = useTranslation(['sourceFiles', 'repairs']);
  const toast = useToast();
  const { can } = useWorkspace();
  const canUpload = can('statement.upload');
  const canDelete = can('source_file.delete');
  // The repair's flags: null (no chips, no filter) unless the repair answers.
  const { flags: repairFlags, attentionCount } =
    useStatementRepairFlags(can('repair.review'));
  const [attentionOnly, setAttentionOnly] = useState(false);
  const showAttention = attentionOnly && Boolean(repairFlags);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcessId, setSelectedProcessId] = useUrlSyncedId('process_id', { type: 'string' });
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [isCheckingDelete, setIsCheckingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const pollRef = useRef(null);

  // Account filter state — synced to URL as comma-separated ids
  const [filterAccounts, setFilterAccounts] = useState([]);
  const [accountMenuSearch, setAccountMenuSearch] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get('account_ids');
    if (!raw) return [];
    return raw.split(',').map(Number).filter(Number.isFinite);
  });

  useEffect(() => {
    const url = new URL(window.location);
    if (selectedAccountIds.length > 0) {
      url.searchParams.set('account_ids', selectedAccountIds.join(','));
    } else {
      url.searchParams.delete('account_ids');
    }
    window.history.replaceState({}, '', url);
  }, [selectedAccountIds]);

  // Block filter changes while a (slow) reload is in flight, so rapid toggles
  // can't stack requests or race the list into an inconsistent state.
  const toggleAccountId = useCallback((id) => {
    if (loading) return;
    setSelectedAccountIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, [loading]);

  const clearAccountFilter = useCallback(() => {
    if (loading) return;
    setSelectedAccountIds([]);
  }, [loading]);

  const loadAccounts = useCallback(() => {
    fetchSourceFileAccounts().then(setFilterAccounts).catch(() => {});
  }, []);

  // `silent` skips the loading state — used by the background poll refresh so a
  // live status update doesn't flash the whole-list spinner. Filter changes and
  // the initial load run non-silent so the (slow) fetch shows feedback and the
  // filter controls block until it resolves.
  const loadProcesses = useCallback(async (accountIds, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await listProcesses({
        pageSize: 100,
        accountIds: accountIds?.length ? accountIds : undefined,
      });
      setProcesses(result.processes || []);
    } catch (err) {
      console.error('Failed to load processes:', err);
      toast({
        title: t('toasts.errorTitle'),
        description: t('toasts.loadFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast, t]);

  const loadCoverage = useCallback((accountIds) => {
    const firstId = accountIds?.length === 1 ? accountIds[0] : null;
    // The endpoint takes one account; for several, keep the selected ones.
    fetchSourceFilesCoverage(firstId)
      .then((data) => setCoverage(accountIds?.length > 1
        ? { ...data, accounts: data.accounts.filter((a) => accountIds.includes(a.account_id)) }
        : data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadProcesses(selectedAccountIds);
    loadCoverage(selectedAccountIds);
    loadAccounts();
    fetchCategories().then(setCategories).catch(() => {});
    fetchTags().then(setAllTags).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProcesses(selectedAccountIds);
    loadCoverage(selectedAccountIds);
  }, [selectedAccountIds, loadProcesses, loadCoverage]);

  // Polling: refresh active processes individually so the list pill updates live
  useEffect(() => {
    const active = processes.filter((p) => isActiveStatus(p.status));
    if (active.length === 0) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    const tick = async () => {
      const updates = await Promise.allSettled(
        active.map((p) => getProcessStatus(p.process_id))
      );
      setProcesses((prev) =>
        prev.map((p) => {
          const idx = active.findIndex((a) => a.process_id === p.process_id);
          if (idx === -1) return p;
          const r = updates[idx];
          if (r.status !== 'fulfilled') return p;
          return { ...p, ...r.value };
        })
      );
      // If any active just transitioned to terminal, refresh full list to ensure
      // source_file metadata (account, size) is up-to-date
      const anyTerminal = updates.some(
        (r) => r.status === 'fulfilled' && !isActiveStatus(r.value.status)
      );
      if (anyTerminal) {
        loadProcesses(selectedAccountIds, { silent: true });
        loadCoverage(selectedAccountIds);
        loadAccounts();
      }
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [processes, loadProcesses, loadCoverage, loadAccounts, selectedAccountIds]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return processes.filter((p) => {
      if (showAttention && !repairFlags[p.source_file?.id]?.needs_attention) return false;
      if (!q) return true;
      return p.source_file?.filename?.toLowerCase().includes(q);
    });
  }, [processes, searchTerm, showAttention, repairFlags]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    filtered.forEach((p) => {
      const key = monthKeyOf(uploadDateOf(p));
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(p);
    });
    buckets.forEach((items) =>
      items.sort((a, b) => {
        const ta = new Date(uploadDateOf(a) || 0).getTime();
        const tb = new Date(uploadDateOf(b) || 0).getTime();
        return tb - ta;
      })
    );
    // Most recent month first; "unknown" pinned to the bottom
    return [...buckets.entries()].sort(([a], [b]) => {
      if (a === 'unknown') return 1;
      if (b === 'unknown') return -1;
      return b.localeCompare(a);
    });
  }, [filtered]);

  const stats = useMemo(() => {
    const completed = processes.filter((p) => p.status === 'completed');
    let movements = 0;
    completed.forEach((p) => {
      const finalStep = p.steps?.find((s) => s.step_number === 5);
      const created = finalStep?.results?.movement?.created?.count;
      if (created) movements += created;
    });

    let coverageStart = null;
    let coverageEnd = null;
    coverage?.accounts?.forEach((acc) => {
      acc.periods?.forEach((period) => {
        const start = new Date(period.start_date).getTime();
        const end = new Date(period.end_date).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) return;
        if (!coverageStart || start < coverageStart) coverageStart = start;
        if (!coverageEnd || end > coverageEnd) coverageEnd = end;
      });
    });

    return {
      filesCount: completed.length,
      movements,
      range:
        coverageStart && coverageEnd
          ? `${formatShortDate(coverageStart)} – ${formatShortDate(coverageEnd)}`
          : '—',
    };
  }, [processes, coverage]);

  const selectedProcess = useMemo(
    () => processes.find((p) => p.process_id === selectedProcessId) || null,
    [processes, selectedProcessId]
  );

  // A link to a statement by its file (`?source_file_id=`, a chip in the chat,
  // MI-343). The page selects by upload, so the file's upload is looked up once
  // the list is in, and the file then leaves the address: `process_id` says
  // which statement is open from there on. Once per navigation.
  const location = useLocation();
  const followedFile = useRef(null);
  useEffect(() => {
    if (loading || followedFile.current === location.key) return;
    followedFile.current = location.key;
    const fileId = Number(new URLSearchParams(location.search).get('source_file_id'));
    if (!fileId) return;
    const upload = processes.find((p) => Number(p.source_file?.id) === fileId);
    if (upload) setSelectedProcessId(upload.process_id);
    const url = new URL(window.location);
    url.searchParams.delete('source_file_id');
    window.history.replaceState({}, '', url);
  }, [loading, location.key, location.search, processes, setSelectedProcessId]);

  const handleSelect = useCallback((process) => {
    setSelectedProcessId(process.process_id);
  }, [setSelectedProcessId]);

  const handleClosePanel = useCallback(() => {
    setSelectedProcessId(null);
  }, [setSelectedProcessId]);

  const handleDeleteRequest = useCallback(
    async (sourceFile) => {
      setFileToDelete(sourceFile);
      setDeleteImpact(null);
      onDeleteOpen();
      setIsCheckingDelete(true);
      try {
        const impact = await deleteSourceFile(sourceFile.id, true);
        setDeleteImpact(impact);
      } catch (error) {
        onDeleteClose();
        let msg = t('toasts.deleteImpactFailed');
        if (error.response?.status === 409) {
          msg = error.response.data?.detail || t('toasts.deleteJobInFlight');
        } else if (error.response?.status === 404) {
          msg = t('toasts.fileNotFound');
        }
        toast({ title: t('toasts.errorTitle'), description: msg, status: 'error', duration: 6000, isClosable: true });
      } finally {
        setIsCheckingDelete(false);
      }
    },
    [onDeleteOpen, onDeleteClose, toast, t]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!fileToDelete) return;
    setIsDeleting(true);
    try {
      await deleteSourceFile(fileToDelete.id);
      toast({
        title: t('toasts.deletedTitle'),
        description: t('toasts.deletedDescription'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setFileToDelete(null);
      setDeleteImpact(null);
      setSelectedProcessId(null);
      await loadProcesses(selectedAccountIds);
      loadCoverage(selectedAccountIds);
      loadAccounts();
    } catch (error) {
      const msg = error.response?.data?.detail || t('toasts.deleteFailed');
      toast({ title: t('toasts.errorTitle'), description: msg, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsDeleting(false);
    }
  }, [fileToDelete, onDeleteClose, toast, loadProcesses, loadCoverage, loadAccounts, selectedAccountIds, t, setSelectedProcessId]);

  const handleRetry = useCallback(
    async (processId) => {
      try {
        const result = await retryProcess(processId);
        toast({ title: t('toasts.retryingTitle'), status: 'info', duration: 3000, isClosable: true });
        // Adopt the new process_id if backend rotates it
        if (result?.process_id && result.process_id !== processId) {
          setSelectedProcessId(result.process_id);
        }
        await loadProcesses(selectedAccountIds);
      } catch (error) {
        toast({
          title: t('toasts.retryFailedTitle'),
          description: error.response?.data?.detail || t('toasts.retryFailedDescription'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast, loadProcesses, selectedAccountIds, t, setSelectedProcessId]
  );

  const handleCancel = useCallback(
    async (processId) => {
      try {
        await cancelProcess(processId);
        toast({ title: t('toasts.cancelledTitle'), status: 'info', duration: 3000, isClosable: true });
        await loadProcesses(selectedAccountIds);
      } catch (error) {
        toast({
          title: t('toasts.cancelFailedTitle'),
          description: error.response?.data?.detail || t('toasts.cancelFailedDescription'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [toast, loadProcesses, selectedAccountIds, t]
  );

  const handleUploadStarted = useCallback(() => {
    loadProcesses(selectedAccountIds);
    loadCoverage(selectedAccountIds);
    loadAccounts();
  }, [loadProcesses, loadCoverage, loadAccounts, selectedAccountIds]);

  const isPanelOpen = selectedProcess !== null;
  // Same drag-to-size panel as the Movements page.
  const panelResize = useResizablePanel(isPanelOpen, pagePanelOptions(440));

  return (
    <Flex ref={panelResize.containerRef} h={{ base: 'calc(100vh - 56px)', md: 'var(--mk-page-h)' }} overflow="hidden" position="relative">
      {/* Left side: list + uploader */}
      <Box flex={1} display="flex" flexDirection="column" minW={0}>
        <Box
          borderBottom="1px solid"
          borderColor="redesign.border"
          flexShrink={0}
        >
        {/* The wrapper draws the hairline under the title row and the chosen
            accounts alike, so the page header drops its own. */}
        <PageHeader title={t('title')} borderBottom="none">
          <HStack spacing={2}>
            <InputGroup size="sm" maxW="240px">
              <InputLeftElement pointerEvents="none" h="32px">
                <SearchIcon color="redesign.text.muted" boxSize={3} />
              </InputLeftElement>
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                isDisabled={loading}
                fontSize="sm"
                h="32px"
              />
            </InputGroup>
            {repairFlags && (
              <Button
                size="sm"
                h="32px"
                variant="outline"
                flexShrink={0}
                fontSize="xs"
                fontWeight={500}
                borderColor={attentionOnly ? 'redesign.accent.blue' : 'redesign.border.strong'}
                color={attentionOnly ? 'redesign.accent.blue' : 'redesign.text'}
                aria-pressed={attentionOnly}
                onClick={() => setAttentionOnly((on) => !on)}
              >
                {t('repairs:filter.needsAttention', { count: attentionCount })}
              </Button>
            )}
            <Menu closeOnSelect={false}>
              <MenuButton
                as={IconButton}
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="4" y1="12" x2="20" y2="12" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="4" y1="18" x2="20" y2="18" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                }
                size="sm"
                variant="ghost"
                isDisabled={loading}
                color={selectedAccountIds.length > 0 ? 'redesign.accent.blue' : 'redesign.text.muted'}
                _hover={{ color: 'redesign.text', bg: 'transparent' }}
                aria-label={t('filter.byAccount')}
                minW="auto"
                h="auto"
                p={0}
              />
              <Portal>
                <MenuList
                  zIndex={1400}
                  py={1}
                  minW={{ base: '140px', md: '200px' }}
                  maxH="60vh"
                  overflowY="auto"
                  borderRadius="lg"
                  bg="redesign.bg.popup"
                  border="1px solid"
                  borderColor="redesign.border"
                >
                  {/* Search input */}
                  <Box px={{ base: 2, md: 3 }} py={1.5} display="flex" alignItems="center" gap={1.5}>
                    <SearchIcon color="redesign.text.muted" boxSize={3} flexShrink={0} />
                    <Box
                      as="span"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={(e) => setAccountMenuSearch(e.currentTarget.textContent || '')}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      minW="60px"
                      lineHeight="22px"
                      fontSize={{ base: 'xs', md: 'sm' }}
                      color="redesign.text"
                      outline="none"
                      display="inline-block"
                      verticalAlign="middle"
                      _empty={{
                        _before: {
                          content: `"${t('filter.searchPlaceholder')}"`,
                          color: 'redesign.text.muted',
                        },
                      }}
                    />
                  </Box>
                  <Divider mb={1} />
                  {(() => {
                    const q = accountMenuSearch.trim().toLowerCase();
                    const visible = q
                      ? filterAccounts.filter((a) => (a.name || '').toLowerCase().includes(q))
                      : filterAccounts;
                    if (filterAccounts.length === 0) {
                      return (
                        <MenuItem
                          isDisabled
                          py={{ base: 1, md: 2 }}
                          px={{ base: 2, md: 3 }}
                          minH={{ base: '28px', md: '36px' }}
                        >
                          <Text fontSize={{ base: 'xs', md: 'sm' }} color="redesign.text.muted">
                            {t('filter.noAccounts')}
                          </Text>
                        </MenuItem>
                      );
                    }
                    if (visible.length === 0) {
                      return (
                        <MenuItem
                          isDisabled
                          py={{ base: 1, md: 2 }}
                          px={{ base: 2, md: 3 }}
                          minH={{ base: '28px', md: '36px' }}
                        >
                          <Text fontSize={{ base: 'xs', md: 'sm' }} color="redesign.text.muted">
                            {t('filter.noMatch')}
                          </Text>
                        </MenuItem>
                      );
                    }
                    return visible.map((acct) => {
                      const isSelected = selectedAccountIds.includes(acct.id);
                      return (
                        <MenuItem
                          key={acct.id}
                          onClick={() => toggleAccountId(acct.id)}
                          py={{ base: 1, md: 2 }}
                          px={{ base: 2, md: 3 }}
                          minH={{ base: '28px', md: '36px' }}
                          bg={isSelected ? 'redesign.bg.hover' : undefined}
                          _hover={{ bg: 'redesign.bg.hover' }}
                        >
                          <Flex align="center" gap={2} w="100%">
                            <Box
                              w="14px"
                              h="14px"
                              borderRadius="sm"
                              border="1.5px solid"
                              borderColor={isSelected ? 'redesign.accent.fill' : 'redesign.checkbox.border'}
                              bg={isSelected ? 'redesign.accent.fill' : 'transparent'}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              {isSelected && (
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                  <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </Box>
                            <Text fontWeight="500" fontSize={{ base: 'xs', md: 'sm' }}>
                              {acct.icon ? `${acct.icon} ` : ''}{acct.name || `Account ${acct.id}`}
                            </Text>
                          </Flex>
                        </MenuItem>
                      );
                    });
                  })()}
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </PageHeader>

        {selectedAccountIds.length > 0 && (
          <Flex
            px={{ base: 3, md: 5 }}
            gap={2}
            mt={0}
            pb={2}
            align="center"
            flexWrap="wrap"
            flexShrink={0}
            opacity={loading ? 0.6 : 1}
            pointerEvents={loading ? 'none' : 'auto'}
          >
            {selectedAccountIds.map((id) => {
              const acct = filterAccounts.find((a) => a.id === id);
              return (
                <Flex
                  key={id}
                  align="center"
                  gap={0}
                  borderRadius="full"
                  overflow="hidden"
                  h="28px"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="6px"
                    bg="redesign.bg.card"
                    color="redesign.text"
                    pl={2.5}
                    pr={2.5}
                    h="100%"
                    fontSize="xs"
                  >
                    {acct?.icon ? `${acct.icon} ` : ''}{acct?.name || `Account ${id}`}
                  </Box>
                  <Box
                    as="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="redesign.bg.card"
                    color="redesign.text"
                    borderLeft="1px solid"
                    borderColor="redesign.border"
                    px={2}
                    h="100%"
                    cursor="pointer"
                    _hover={{ bg: 'redesign.bg.hover' }}
                    onClick={() => toggleAccountId(id)}
                    aria-label={`Remove ${acct?.name || id}`}
                  >
                    <CloseIcon boxSize="7px" />
                  </Box>
                </Flex>
              );
            })}
            <Button
              size="xs"
              variant="link"
              color="redesign.text.muted"
              fontWeight="normal"
              ml="auto"
              onClick={clearAccountFilter}
              isDisabled={loading}
              _hover={{ color: 'redesign.text' }}
            >
              {t('filter.clearAll')}
            </Button>
          </Flex>
        )}
        </Box>

        <Box flex={1} overflowY="auto" px={{ base: 3, md: 5 }} py={5}>
          <CheckupBanner flags={repairFlags} />
          {canUpload && (
            <Box mb={5}>
              <SourceFilesUploader onUploadStarted={handleUploadStarted} />
            </Box>
          )}

          <HStack
            spacing={4}
            justify="space-around"
            p={4}
            mb={5}
            {...cardSurface}
          >
            <Box textAlign="center" flex={1}>
              <Text fontSize="xl" fontWeight={600} color="redesign.heading">
                {stats.filesCount}
              </Text>
              <Text textStyle="microLabel">
                {t('stats.filesImported')}
              </Text>
            </Box>
            <Box w="1px" h="32px" bg="redesign.border" />
            <Box textAlign="center" flex={1}>
              <Text fontSize="xl" fontWeight={600} color="redesign.heading">
                {stats.movements}
              </Text>
              <Text textStyle="microLabel">
                {t('stats.movementsExtracted')}
              </Text>
            </Box>
            <Box w="1px" h="32px" bg="redesign.border" />
            <Box textAlign="center" flex={1}>
              <Text fontSize="sm" color="redesign.text">
                {stats.range}
              </Text>
              <Text textStyle="microLabel">
                {t('stats.periodCovered')}
              </Text>
            </Box>
          </HStack>

          {loading ? (
            <Center py={12}>
              <VStack spacing={3}>
                <Spinner color="redesign.text.muted" />
                <Text fontSize="sm" color="redesign.text.muted">
                  {t('list.loading')}
                </Text>
              </VStack>
            </Center>
          ) : filtered.length === 0 ? (
            <Center py={12}>
              <VStack spacing={2}>
                <Text fontSize="md" color="redesign.text.muted">
                  {showAttention && !searchTerm
                    ? t('repairs:filter.empty')
                    : searchTerm ? t('list.withSearch') : t('list.noFiles')}
                </Text>
                <Text fontSize="xs" color="redesign.text.muted">
                  {showAttention && !searchTerm
                    ? t('repairs:filter.emptyHint')
                    : searchTerm ? t('list.withSearchHint') : t('list.noFilesHint')}
                </Text>
              </VStack>
            </Center>
          ) : (
            grouped.map(([key, items]) => (
              <MonthGroup
                key={key}
                monthKey={key}
                items={items}
                selectedProcessId={selectedProcessId}
                onSelect={handleSelect}
                repairFlags={repairFlags}
              />
            ))
          )}
        </Box>
      </Box>

      {/* Right side: detail panel */}
      <Box
        display={{ base: isPanelOpen ? 'block' : 'none', md: 'block' }}
        position={{ base: 'absolute', md: 'relative' }}
        right={{ base: 0, md: 'auto' }}
        top={{ base: 0, md: 'auto' }}
        w={{ base: '100%', md: resizablePanelWidth('44%', 440), lg: resizablePanelWidth('40%', 440) }}
        h="100%"
        bg="redesign.bg.surface"
        borderLeft="1px solid"
        borderLeftColor="redesign.border"
        boxShadow={{ base: '-2px 0 8px rgba(0,0,0,0.15)', md: 'none' }}
        zIndex={{ base: 10, md: 'auto' }}
      >
        <PanelResizeHandle resize={panelResize} />
        <SourceFileDetailPanel
          process={selectedProcess}
          onClose={handleClosePanel}
          onDeleteRequest={canDelete ? handleDeleteRequest : undefined}
          onRetry={handleRetry}
          onCancel={handleCancel}
          categories={categories}
          allTags={allTags}
          repairFlags={repairFlags?.[selectedProcess?.source_file?.id]}
        />
      </Box>

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          onDeleteClose();
          setFileToDelete(null);
          setDeleteImpact(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={isDeleting || isCheckingDelete}
      >
        {isCheckingDelete ? (
          <Center py={6}>
            <Spinner size="md" />
          </Center>
        ) : (
          <Box>
            <Text mb={2} fontSize="sm">{t('deleteDialog.fileToBeDeleted')}</Text>
            {fileToDelete && (
              <Flex
                align="center"
                gap={2}
                py={2}
                px={3}
                bg="redesign.bg.card"
                borderRadius="md"
                mb={3}
              >
                <Icon
                  as={fileMeta(fileToDelete.file_type).icon}
                  color={fileMeta(fileToDelete.file_type).color}
                  boxSize="16px"
                />
                <Text fontSize="sm" fontWeight={600} noOfLines={1}>
                  {fileToDelete.filename}
                </Text>
              </Flex>
            )}
            {deleteImpact?.movement_items?.length > 0 && (
              <Box mb={3}>
                <Text mb={1} fontSize="sm">
                  {deleteImpact.movement_items.length === 1
                    ? t('deleteDialog.movementToBeDeleted')
                    : t('deleteDialog.movementsToBeDeleted', { count: deleteImpact.movement_items.length })}
                </Text>
                <Box maxH="200px" overflowY="auto">
                  {deleteImpact.movement_items.map((mov) => (
                    <Flex
                      key={mov.id}
                      py={1}
                      px={2}
                      borderLeft="3px solid"
                      borderLeftColor="redesign.accent.blue"
                      bg="redesign.bg.card"
                      align="center"
                      mb="1px"
                    >
                      <Box flex={1} minW={0}>
                        <Text fontSize="xs" fontWeight={600} noOfLines={1}>
                          {mov.note || t('deleteDialog.noNote')}
                        </Text>
                        <Text fontSize="2xs" color="redesign.text.muted" noOfLines={1}>
                          {mov.account?.name || t('deleteDialog.unknownAccount')}
                          {mov.date ? ` · ${formatRelativeTime(mov.date)}` : ''}
                        </Text>
                      </Box>
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        ml={2}
                        color={
                          mov.type === 'Income'
                            ? 'redesign.amount.income'
                            : mov.type === 'Expenses'
                            ? 'redesign.amount.expense'
                            : 'redesign.amount.neutral'
                        }
                      >
                        {formatMoney(mov.amount, mov.currency)}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Box>
            )}
            <Flex align="center" gap={2} {...TINT.red} px={3} py={2} borderRadius="md">
              <WarningIcon color="redesign.accent.red" boxSize={3.5} flexShrink={0} />
              <Text fontSize="sm" color="redesign.accent.red" fontWeight={500}>
                {t('deleteDialog.irreversible')}
              </Text>
            </Flex>
          </Box>
        )}
      </ConfirmationDialog>
    </Flex>
  );
}
